/**
 * The client half of the OAuth2 flow.
 *
 * THE ACCESS TOKEN LIVES IN A MODULE VARIABLE, never in localStorage. Anything in
 * localStorage is readable by any script that gets injected into the page, and a token
 * there survives the tab. In memory it dies with the tab and is not reachable through
 * the DOM.
 *
 * THE REFRESH TOKEN IS NEVER TOUCHED HERE AT ALL. The server sets it as an httpOnly
 * cookie scoped to /api/auth, so the browser presents it on a refresh and JavaScript
 * cannot read it — which is what stops an XSS bug from walking away with a 30-day
 * credential. `credentials: 'include'` is what lets the cookie ride along.
 *
 * Sessions survive a reload because of that cookie: on boot the app asks for a new access
 * token, and the cookie is the whole proof.
 */
let accessToken = null;
let refreshing = null;

export const getToken = () => accessToken;
export const setToken = t => { accessToken = t; };

async function requestToken(body) {
	const res = await fetch('/api/auth/token', {
		method: 'POST',
		headers: {'Content-Type': 'application/json'},
		credentials: 'include',
		body: JSON.stringify(body),
	});
	const json = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(json.error_description ?? json.error ?? 'Inloggning misslyckades'), {code: json.error});
	accessToken = json.access_token;
	return json;
}

export const login = (email, password) =>
	requestToken({grant_type: 'password', username: email, password});

/**
 * Concurrent 401s must produce ONE refresh, not one per request. Without the shared
 * promise, four parallel calls each rotate the token and three of them then present a
 * token that has already been rotated — which the server correctly reads as reuse and
 * kills the whole session.
 */
export function refresh() {
	refreshing ??= requestToken({grant_type: 'refresh_token'})
		.finally(() => { refreshing = null; });
	return refreshing;
}

export async function logout() {
	accessToken = null;
	await fetch('/api/auth/revoke', {method: 'POST', credentials: 'include'}).catch(() => {});
}

/**
 * Drop-in for fetch that attaches the token and retries ONCE after refreshing. The retry
 * is what makes a fifteen-minute access token invisible: the user never sees a session
 * expire mid-click, they just occasionally make one extra round trip.
 */
export async function api(path, options = {}) {
	const send = () => fetch(path, {
		...options,
		credentials: 'include',
		headers: {
			...(options.body ? {'Content-Type': 'application/json'} : {}),
			...options.headers,
			...(accessToken ? {Authorization: `Bearer ${accessToken}`} : {}),
		},
	});

	let res = await send();
	if (res.status === 401 && accessToken !== null) {
		try { await refresh(); res = await send(); } catch { /* fall through as 401 */ }
	}
	return res;
}

/** JSON convenience — every existing caller wanted this shape anyway. */
export async function apiJson(path, options) {
	const res = await api(path, options);
	const json = await res.json().catch(() => ({}));
	if (!res.ok) throw Object.assign(new Error(json.error ?? `API svarade ${res.status}`), {status: res.status, body: json});
	return json;
}
